
import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cva, type VariantProps } from "class-variance-authority";
import { useTracking } from "@/utils/eventTracking";

const buttonVariants = cva("font-medium transition-all flex items-center justify-center gap-1 shadow-sm", {
  variants: {
    size: {
      sm: "px-4 py-2 text-sm rounded-lg",
      md: "px-5 py-3 text-base rounded-xl",
      lg: "px-6 py-3.5 text-lg rounded-xl"
    },
    variant: {
      primary: "bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700",
      secondary: "bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700",
      outline: "border-2 border-blue-500 text-blue-600 bg-white hover:bg-blue-50",
      full: "w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700"
    }
  },
  defaultVariants: {
    size: "md",
    variant: "primary"
  }
});

export interface LainaCtaProps extends VariantProps<typeof buttonVariants> {
  to?: string;
  text: string;
  showArrow?: boolean;
  onClick?: () => void;
  className?: string;
  dataTestId?: string;
  isExternal?: boolean;
  trackingLabel?: string;
  location?: string;
}

export const LainaCta: React.FC<LainaCtaProps> = ({
  to = "/kilpailuta",
  text = "Kilpailuta lainasi",
  showArrow = true,
  onClick,
  size,
  variant,
  className = "",
  dataTestId = "laina-cta",
  isExternal = false,
  trackingLabel,
  location
}) => {
  const buttonClasses = `${buttonVariants({
    size,
    variant
  })} ${className}`;
  
  const { trackCta } = useTracking();
  
  const handleClick = () => {
    // Track the click event for analytics with appropriate label
    const label = trackingLabel || text;
    trackCta(label, location);

    // Also use the legacy direct GTM tracking for backward compatibility
    if (window.gtag) {
      window.gtag('event', 'cta_click', {
        'event_category': 'engagement',
        'event_label': label,
        'value': 1
      });
    }

    // Call the onClick prop if provided
    if (onClick) {
      onClick();
    }
  };

  // Ensure text is never empty - fallback to default
  const displayText = text || "Kilpailuta lainasi";
  
  // Force text color based on variant to ensure visibility and contrast
  const content = <>
      {variant === 'outline' ? (
        <span className="text-blue-600 font-semibold">{displayText}</span>
      ) : (
        <span className="text-white font-semibold">{displayText}</span>
      )}
      
      {showArrow && (
        <ArrowRight 
          className={`${size === "sm" ? "h-4 w-4" : "h-5 w-5"} ${
            variant === 'outline' ? 'text-blue-600' : 'text-white'
          }`} 
        />
      )}
    </>;
    
  if (isExternal) {
    return <a href={to} className={buttonClasses} onClick={handleClick} target="_blank" rel="noopener noreferrer" data-testid={dataTestId}>
        {content}
      </a>;
  }
  
  return <Link to={to} className={buttonClasses} onClick={handleClick} data-testid={dataTestId}>
      {content}
    </Link>;
};
